import React from 'react';
import { X, ExternalLink } from 'lucide-react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';
import { Link } from 'react-router-dom';

const BundlePreviewModal = ({ bundle, onClose, onAddBundle, preview = false }) => {
  if (!bundle) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-background rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-border animate-in fade-in zoom-in-95 duration-200 relative" onClick={(e) => e.stopPropagation()}>
        {preview && (
          <div className="bg-blue-600 text-white px-6 py-3 flex items-center justify-between z-20 shadow-md flex-shrink-0">
            <h2 className="text-lg font-bold flex items-center gap-2 tracking-widest uppercase">
              <span className="material-icons">visibility</span> PREVIEW MODE
            </h2>
            <p className="text-sm opacity-90 font-medium">This is how buyers will see your bundle</p>
          </div>
        )}
        
        {/* Header */}
        <div className="p-6 border-b border-border flex justify-between items-start bg-card gap-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 border border-border">
                {bundle.image || bundle.imageUrl || bundle.image_url ? (
                    <Image src={bundle.image || bundle.imageUrl || bundle.image_url} alt={bundle.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <span className="material-icons text-3xl">inventory_2</span>
                    </div>
                )}
            </div>
            <div>
                <h2 className="text-2xl font-bold text-foreground">{bundle.name}</h2>
                <p className="text-muted-foreground mt-1 line-clamp-2">{bundle.description || `${bundle.itemCount} items included`}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-muted rounded-full flex-shrink-0">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-muted/10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"> {/* Smaller cards (more columns) */}
            {bundle.items.map((item, idx) => {
                // Correct URL for Model Viewer
                const linkUrl = item.productId 
                    ? `/3d-model-viewer?id=${item.productId}${item.componentId ? `&componentId=${item.componentId}&componentName=${encodeURIComponent(item.originalName || item.name || '')}` : ''}`
                    : '#';

                return (
                  <div key={idx} className="border border-border rounded-lg overflow-hidden hover:shadow-md transition-all bg-card group relative">
                    <div className="aspect-square bg-muted relative overflow-hidden">
                      <Image 
                        src={item.thumbnail} 
                        alt={item.thumbnailAlt} 
                        className="w-full h-full object-cover transition-transform group-hover:scale-105" 
                      />
                      
                      {/* Component Label - Bottom Left Overlay */}
                      {item.componentId && (
                        <div className="absolute bottom-2 left-2 z-10">
                            <span className="text-[10px] uppercase tracking-wider text-white bg-black/60 backdrop-blur-sm px-1.5 py-0.5 rounded font-medium">
                                Component
                            </span>
                        </div>
                      )}

                      {/* Deep Link Overlay */}
                      <Link 
                        to={linkUrl}
                        target="_blank"
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-20"
                        title="View in Model Viewer"
                      >
                        <span className="bg-white text-black px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                            View 3D <ExternalLink size={12} />
                        </span>
                      </Link>
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-foreground text-xs truncate mb-1" title={item.name}>{item.name}</h4>
                      
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-border">
                        <span className="text-muted-foreground text-[10px]">Qty: <span className="font-medium text-foreground">{item.quantity}</span></span>
                        <span className="font-semibold text-xs">{formatPrice(item.price)}</span>
                      </div>
                    </div>
                  </div>
                );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-card flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-bold text-foreground">{formatPrice(bundle.bundlePrice)}</span>
            {bundle.originalPrice > bundle.bundlePrice && (
                <span className="text-sm text-muted-foreground line-through">{formatPrice(bundle.originalPrice)}</span>
            )}
            {bundle.savings > 0 && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Save {formatPrice(bundle.savings)}</span>
            )}
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-none">Close</Button>
            <Button onClick={() => onAddBundle(bundle.id)} className="flex-1 sm:flex-none" iconName="ShoppingCart" disabled={preview}>
              {preview ? 'Preview Mode' : 'Add Bundle to Cart'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundlePreviewModal;